/**
 * EmissionsByScopeChart Component
 * A stacked bar chart showing CO2e emissions broken down by scope over time.
 * Supports toggle between per-dish and total emissions view.
 */

import { formatNumberEs } from '../../utils/formatters';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from './Card';
import { LoadingSpinner } from './LoadingSpinner';
import {
  CalendarizedEmissionsByScopeResponse,
  CoversVolume,
} from '../../types/api';
import { findCoversForMonth } from '../../utils/perDishCalculations';

// ============================================================================
// Types
// ============================================================================

export type DisplayMode = 'per_dish' | 'total';

export interface EmissionsByScopeChartProps {
  data?: CalendarizedEmissionsByScopeResponse | undefined;
  coversData?: CoversVolume[] | undefined;
  loading?: boolean;
  error?: string | undefined;
  className?: string;
}

export interface EmissionsByScopeChartDatum {
  date: string;
  formatted_date: string;
  scope_1_co2e: number;
  scope_2_co2e: number;
  scope_3_co2e: number;
  total_co2e: number;
  covers_count: number;
  has_sales_data: boolean;
}

interface TransformArgs {
  response?: CalendarizedEmissionsByScopeResponse | undefined;
  coversData?: CoversVolume[] | undefined;
  displayMode: DisplayMode;
}

export function transformEmissionsByScopeChartData({
  response,
  coversData = [],
  displayMode,
}: TransformArgs): EmissionsByScopeChartDatum[] {
  if (!response?.data) {
    return [];
  }

  return response.data.map((monthData) => {
    const emissions = monthData.emissions ?? {};

    const scope1Total =
      typeof emissions.scope_1_kg_co2e === 'number'
        ? emissions.scope_1_kg_co2e
        : 0;
    const scope2Total =
      typeof emissions.scope_2_kg_co2e === 'number'
        ? emissions.scope_2_kg_co2e
        : 0;
    const scope3Total =
      typeof emissions.scope_3_kg_co2e === 'number'
        ? emissions.scope_3_kg_co2e
        : 0;
    const total =
      typeof emissions.total_kg_co2e === 'number'
        ? emissions.total_kg_co2e
        : scope1Total + scope2Total + scope3Total;

    const coversInfo = findCoversForMonth(
      coversData,
      monthData.year,
      monthData.month,
    );

    const shouldNormalize =
      displayMode === 'per_dish' &&
      coversInfo.has_data &&
      coversInfo.covers_count > 0;
    const divisor = shouldNormalize ? coversInfo.covers_count : 1;

    return {
      date: `${monthData.year}-${String(monthData.month).padStart(2, '0')}`,
      formatted_date: monthData.month_name,
      scope_1_co2e: scope1Total / divisor,
      scope_2_co2e: scope2Total / divisor,
      scope_3_co2e: scope3Total / divisor,
      total_co2e: total / divisor,
      covers_count: coversInfo.covers_count,
      has_sales_data: coversInfo.has_data,
    };
  });
}

// ============================================================================
// Styled Components
// ============================================================================

const ChartContainer = styled(Card)`
  padding: 1.5rem;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 400px;

  @media (max-width: 768px) {
    padding: 1rem;
    min-height: 350px;
  }
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const ChartTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);

  @media (max-width: 768px) {
    font-size: 1.1rem;
    text-align: left;
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--surface);

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ToggleButton = styled.button<{ $isActive: boolean }>`
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${(props) =>
    props.$isActive ? 'var(--action-primary)' : 'var(--surface)'};
  color: ${(props) => (props.$isActive ? 'white' : 'var(--text-secondary)')};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 40px;

  &:hover {
    background: ${(props) =>
      props.$isActive
        ? 'var(--action-primary-hover)'
        : 'var(--interactive-hover)'};
    color: ${(props) => (props.$isActive ? 'white' : 'var(--text-primary)')};
  }

  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
    outline-offset: -3px;
  }

  @media (max-width: 768px) {
    flex: 1;
    min-height: 44px;
    padding: 0.6rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const UnitLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
`;

const ChartContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  color: var(--danger-600);
  text-align: center;
`;

const EmptyMessage = styled.div`
  color: var(--text-secondary);
  text-align: center;
`;

// ============================================================================
// Custom Tooltip
// ============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value?: number;
    dataKey: string;
    name: string;
    color: string;
    payload?: EmissionsByScopeChartDatum;
  }>;
  label?: string;
  displayMode: DisplayMode;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  displayMode,
}) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    const unitSuffix = displayMode === 'per_dish' ? '/plato' : '';

    return (
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: '0.75rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <p
          style={{
            fontWeight: 600,
            margin: '0 0 0.5rem 0',
            color: 'var(--text-primary)',
          }}
        >
          {label}
        </p>
        {payload.map((entry) => {
          if (typeof entry.value !== 'number') return null;
          return (
            <p
              key={entry.dataKey}
              style={{ color: entry.color, margin: '0.25rem 0' }}
            >
              <strong>{entry.name}:</strong> {formatNumberEs(entry.value, 4)}{' '}
              kgCO₂e
              {unitSuffix}
            </p>
          );
        })}
        {displayMode === 'per_dish' && data?.covers_count && (
          <p
            style={{
              color: 'var(--text-secondary)',
              fontSize: '0.8rem',
              margin: '0.5rem 0 0 0',
            }}
          >
            ({data.covers_count} cubiertos)
          </p>
        )}
        {displayMode === 'per_dish' && !data?.has_sales_data && (
          <p
            style={{
              color: 'var(--warning-600)',
              fontSize: '0.75rem',
              margin: '0.25rem 0 0 0',
            }}
          >
            ⚠️ Sin datos de ventas
          </p>
        )}
      </div>
    );
  }
  return null;
};

// ============================================================================
// Component
// ============================================================================

export const EmissionsByScopeChart: React.FC<EmissionsByScopeChartProps> = ({
  data,
  coversData = [],
  loading = false,
  error,
  className,
}) => {
  const [displayMode, setDisplayMode] = useState<DisplayMode>('total');

  const chartData = useMemo(
    () =>
      transformEmissionsByScopeChartData({
        response: data,
        coversData,
        displayMode,
      }),
    [data, coversData, displayMode],
  );

  // Generate unit display
  const getDisplayUnit = (): string => {
    return displayMode === 'per_dish' ? 'kgCO₂e/plato' : 'kgCO₂e';
  };

  if (loading) {
    return (
      <ChartContainer className={className}>
        <ChartHeader>
          <div>
            <ChartTitle>Emisiones por Alcance</ChartTitle>
            <UnitLabel>{getDisplayUnit()}</UnitLabel>
          </div>
        </ChartHeader>
        <ChartContent>
          <LoadingSpinner />
        </ChartContent>
      </ChartContainer>
    );
  }

  if (error) {
    return (
      <ChartContainer className={className}>
        <ChartHeader>
          <div>
            <ChartTitle>Emisiones por Alcance</ChartTitle>
            <UnitLabel>{getDisplayUnit()}</UnitLabel>
          </div>
        </ChartHeader>
        <ChartContent>
          <ErrorMessage>{error}</ErrorMessage>
        </ChartContent>
      </ChartContainer>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <ChartContainer className={className}>
        <ChartHeader>
          <div>
            <ChartTitle>Emisiones por Alcance</ChartTitle>
            <UnitLabel>{getDisplayUnit()}</UnitLabel>
          </div>
          <ToggleGroup>
            <ToggleButton
              $isActive={displayMode === 'per_dish'}
              onClick={() => setDisplayMode('per_dish')}
              aria-label="Mostrar emisiones por plato"
            >
              Por plato
            </ToggleButton>
            <ToggleButton
              $isActive={displayMode === 'total'}
              onClick={() => setDisplayMode('total')}
              aria-label="Mostrar emisiones totales"
            >
              Total
            </ToggleButton>
          </ToggleGroup>
        </ChartHeader>
        <ChartContent>
          <EmptyMessage>No hay datos de emisiones para mostrar.</EmptyMessage>
        </ChartContent>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer className={className}>
      <ChartHeader>
        <div>
          <ChartTitle>Emisiones por Alcance</ChartTitle>
          <UnitLabel>{getDisplayUnit()}</UnitLabel>
        </div>
        <ToggleGroup>
          <ToggleButton
            $isActive={displayMode === 'per_dish'}
            onClick={() => setDisplayMode('per_dish')}
            aria-label="Mostrar emisiones por plato"
          >
            Por plato
          </ToggleButton>
          <ToggleButton
            $isActive={displayMode === 'total'}
            onClick={() => setDisplayMode('total')}
            aria-label="Mostrar emisiones totales"
          >
            Total
          </ToggleButton>
        </ToggleGroup>
      </ChartHeader>
      <ChartContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 12, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="formatted_date"
              fontSize={12}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
              stroke="var(--text-secondary)"
            />
            <YAxis
              fontSize={12}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
              stroke="var(--text-secondary)"
            />
            <Tooltip
              content={<CustomTooltip displayMode={displayMode} />}
              cursor={{ fill: 'var(--brand-500-alpha-20)' }}
            />
            <Legend wrapperStyle={{ paddingTop: '15px', fontSize: '12px' }} />
            <Bar
              dataKey="scope_1_co2e"
              stackId="emissions"
              fill="var(--success-600)"
              name="Alcance 1"
            />
            <Bar
              dataKey="scope_2_co2e"
              stackId="emissions"
              fill="var(--info-600)"
              name="Alcance 2"
            />
            <Bar
              dataKey="scope_3_co2e"
              stackId="emissions"
              fill="var(--warning-600)"
              name="Alcance 3"
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContent>
    </ChartContainer>
  );
};
